/**
 * Multiplayer Snakes and Ladders Service
 *
 * Extends GenericMultiplayerService for online play.
 * Game type: 'snakes-ladders'
 * Server generates dice rolls; no move validation needed beyond turn order.
 */

import { WebSocketService } from '../../services/WebSocketService';
import {
  GenericMultiplayerService,
  GameResult,
} from '../../services/multiplayer/GenericMultiplayerService';
import { SnakesLaddersGameState, MoveResult } from '../types';

export class MultiplayerSnakesLaddersService extends GenericMultiplayerService<
  SnakesLaddersGameState,
  MoveResult
> {
  private onDiceResultCallback?: (diceValue: number) => void;

  constructor(wsService: WebSocketService) {
    super(wsService, 'snakes-ladders');
  }

  // =========================================================================
  // Abstract method implementations
  // =========================================================================

  protected setupMessageHandlers(): void {
    this.wsService.setCustomMessageHandler((message: any) => {
      const { action } = message;

      if (!action || !action.startsWith('snakes-ladders:')) return;

      const gameAction = action.replace('snakes-ladders:', '');

      switch (gameAction) {
        case 'roomCreated':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player1';
          this.playerColor = message.playerColor || 'red';
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'roomJoined':
          this.roomCode = message.roomCode;
          this.playerRole = message.playerRole || 'player2';
          this.playerColor = message.playerColor || 'blue';
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'opponentJoined':
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId || 'opponent');
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'gameStarted':
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
          }
          if (this.onGameStartedCallback) {
            this.onGameStartedCallback();
          }
          break;

        case 'diceResult':
          if (this.onDiceResultCallback) {
            this.onDiceResultCallback(message.diceValue);
          }
          break;

        case 'moveResult':
          if (message.move && message.gameState) {
            this.gameState = message.gameState;
            if (this.onMoveUpdateCallback) {
              this.onMoveUpdateCallback(message.move, message.gameState);
            }
          }
          break;

        case 'gameOver':
          if (this.onGameOverCallback) {
            this.onGameOverCallback({
              result: message.result || 'win',
              winner: message.winner,
              reason: message.reason,
            });
          }
          break;

        case 'gameState':
          if (message.gameState) {
            this.gameState = message.gameState;
            if (this.onGameStateReceivedCallback) {
              this.onGameStateReceivedCallback(message.gameState);
            }
          }
          break;

        case 'playerDisconnected':
          if (this.onPlayerDisconnectedCallback) {
            this.onPlayerDisconnectedCallback(message.playerId);
          }
          break;

        case 'playerReconnected':
          if (this.onPlayerConnectedCallback) {
            this.onPlayerConnectedCallback(message.playerId);
          }
          break;

        case 'error':
          this.handleError(message.message || 'Unknown multiplayer error');
          break;

        default:
          console.warn(`Unknown snakes-ladders action: ${gameAction}`);
      }
    });
  }

  protected sendCreateRoomMessage(): void {
    this.wsService.send({
      action: 'snakes-ladders:createRoom',
      playerId: this.playerId,
      gameType: this.gameType,
    });
  }

  protected sendJoinRoomMessage(roomCode: string): void {
    this.wsService.send({
      action: 'snakes-ladders:joinRoom',
      roomCode,
      playerId: this.playerId,
      gameType: this.gameType,
    });
  }

  protected sendLeaveRoomMessage(): void {
    this.wsService.send({
      action: 'snakes-ladders:resign',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  protected getMaxPlayers(): number {
    return 4;
  }

  // =========================================================================
  // Game-specific methods
  // =========================================================================

  /** Send a dice roll request to the server */
  async sendMove(_move: MoveResult): Promise<boolean> {
    try {
      this.wsService.send({
        action: 'snakes-ladders:rollDice',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
      return true;
    } catch (error) {
      this.handleError('Failed to send dice roll', error);
      return false;
    }
  }

  /** Request a dice roll from the server */
  requestDiceRoll(): void {
    this.wsService.send({
      action: 'snakes-ladders:rollDice',
      roomCode: this.roomCode,
      playerId: this.playerId,
    });
  }

  /** Check if it's the current player's turn */
  isMyTurn(currentTurn: string): boolean {
    return this.playerRole === currentTurn || this.playerId === currentTurn;
  }

  /** Register callback for dice results from server */
  onDiceResult(callback: (diceValue: number) => void): void {
    this.onDiceResultCallback = callback;
  }

  /** Send reconnection request */
  reconnect(): void {
    if (this.roomCode) {
      this.wsService.send({
        action: 'snakes-ladders:reconnect',
        roomCode: this.roomCode,
        playerId: this.playerId,
      });
    }
  }
}
